'use client';
import React, { useEffect, useRef } from "react";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import { LanguageType, LanguageDirection } from "@/lib/types";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SplitType from "split-type";
import { useGSAP } from "@gsap/react";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigation";
import useAspectRatio from "@/hooks/useAspectRatio";
import Image from 'next/image';
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';
gsap.registerPlugin(useGSAP, ScrollTrigger); // register the hook to avoid react version discrepancies

interface SummarySectionProps {
  language?: LanguageType
  text: string
  buttonText: string
  imageSrc: string
  imageAlt: string
  onInitialCenterPosition?: (position: number) => void
  onSectionRefReady?: (ref: React.RefObject<HTMLDivElement | null>) => void
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const SummarySection: React.FC<SummarySectionProps> = ({
  language = "english",
  text,
  buttonText,
  imageSrc,
  imageAlt,
  onInitialCenterPosition,
  onSectionRefReady
}) => {
  const navigate = useAnimatedNavigate();
  const aspectRatio = useAspectRatio();
  const isLg = useIsLg();
  const isTb = useIsTb();
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const sectionRefInternal = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {

    if (aspectRatio === 0) {
      return;
    }

    const section = sectionRefInternal.current;
    const container = containerRef.current;
    const textElement = textRef.current;
    const buttonElement = buttonRef.current;
    const content = contentRef.current;
    if (section && container && textElement && buttonElement && content) {

      // Create SplitType instance only for desktop
      const splitText = isLg ? new SplitType(textElement, { types: 'words' }) : null;

      // Set initial states
      if (isLg) {
        gsap.set(splitText!.words, { autoAlpha: 0 });
        gsap.set(container, { scale: 0.75 });
      } else {
        gsap.set(textElement, { autoAlpha: 0 });
        if(isTb) {
          gsap.set(section, { padding: "0 2.5rem" });
        } else {
          gsap.set(section, { padding: "0 0.5rem" });
        }
      }
      gsap.set(container, {
        aspectRatio: isLg ? aspectRatio : '1 / 1', //isTb ? '5 / 3' : '0.5625 / 1',
        scale: isLg ? 0.75 : 1
      });
      gsap.set(buttonElement, { autoAlpha: 0 });

      // Image animation with pinning
      if (isLg) {
        const imageTl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            // start: isLg ? "center center" : "70% bottom",
            // end: isLg ? "+=200%" : "70% bottom",
            start: isLg ? "top center+=30%" : "70% bottom",
            end: isLg ? "top top+=20%" : "70% bottom",
            scrub: 1,
            // pin: isLg,
            // pinSpacing: isLg,
            // anticipatePin: 1,
            id: "summary-pin",
            // fastScrollEnd: true,
          }
        });

        imageTl.to(container, {
          scaleY: 1.01,
          scaleX: 1.00,
          aspectRatio: isLg ? aspectRatio : '1 / 1', //isTb ? '5 / 3' : '0.5625 / 1',
          borderRadius: isLg ? "0px" : '',
          outline: isLg ? "0px" : '',
          duration: 1,
          ease: "power2.out",
          force3D: true,
        });
      }

      // Text and button animation
      const textButtonTl = gsap.timeline({
        scrollTrigger: {
          trigger: content,
          start: isLg ? "top 70%" : "top 80%",
          // start: isLg ? "center center" : "top 80%",
          end: "top 20%",
          // end: "+=200%",
          scrub: isLg,
          // pin: isLg,
          // pinSpacing: isLg,
          once: true,
        },
      });

      if (isLg) {
        // Desktop: Sequential animation
        textButtonTl
          .to(splitText!.words, {
            autoAlpha: 1,
            stagger: 0.15,
            ease: "none",
            force3D: true,
          })
          .to(buttonElement, {
            autoAlpha: 1,
            duration: 0.5,
            ease: "power2.out",
            force3D: true,
          }, ">");
      } else {
        // Mobile: Simpler animation
        textButtonTl
          .to(textElement, {
            autoAlpha: 1,
            duration: 1,
            ease: "power2.out",
            force3D: true,
          })
          .to(buttonElement, {
            autoAlpha: 1,
            duration: 0.3,
            ease: "power2.out",
            force3D: true,
          }, ">");
      }
    }

  }, { dependencies: [isLg, aspectRatio], revertOnUpdate: true });

  // Add useEffect to calculate and pass up initial center position
  useEffect(() => {
    if (containerRef.current && onInitialCenterPosition) {
      const rect = containerRef.current.getBoundingClientRect();
      // const centerY = window.scrollY + rect.top + (rect.height / 2);
      const bottomY = window.scrollY + rect.top + (isLg ? rect.height : (rect.height / 2));
      onInitialCenterPosition(bottomY);
      // onInitialCenterPosition(centerY);
    }
  }, [aspectRatio, isLg, onInitialCenterPosition]); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    if (sectionRefInternal.current && onSectionRefReady) {
      onSectionRefReady(sectionRefInternal);
    }
  }, [onSectionRefReady, aspectRatio]);

  return (
    <section
      ref={sectionRefInternal}
      // padding-narrow-x-mobile
      // padding-narrow-x-desktop
      className="
      relative
      w-full
      flex flex-col
      items-center
      gap-y-[6rem] lg:gap-y-[19.25rem]
      z-0
      ">

      <div
        // aspect-9/16 tb:aspect-5/3
        className="
        relative
        z-10
        w-full
        flex items-center justify-center
        ">
        <div
          ref={containerRef}
          // style={{ aspectRatio: isLg ? aspectRatio : '9/16' }}
          style={{ aspectRatio: isLg ? aspectRatio : '1 / 1' }} //isTb ? '5 / 3' : '5 / 3' }} // 9/16
          // aspect-9/16 tb:aspect-5/3
          // max-h-[100vh]
          className="
            relative
            w-full h-full
            max-w-[100%]
            lg:scale-75
            overflow-hidden
            outline-secondary
            outline-stroke-soft-200
            border-radius-main
            z-10
          ">
          <Image
            src={imageSrc}
            alt={imageAlt}
            fill
            sizes="100vw"
            loading="eager"
            style={{ objectFit: 'cover' }}
          />
        </div>
      </div>

      <div className="
        relative
        z-10
        flex-grow container-small
        px-[0.75rem] lg:px-0
        ">
        <div
          ref={contentRef}
          style={{ display: LANGUAGE_TO_DIRECTION[language] }}
          className="
            w-full
            flex flex-col
            items-center
            gap-[2rem] lg:gap-[3.5rem]
           ">
          <p
            ref={textRef}
            className={`
              text-center
              text-text-reversed
              text-88-to-28--neue-june
            `}>
            {text}
          </p>
          <div
            ref={buttonRef}
            style={{ direction: "ltr" }}
            className="w-fit"
          >
            <PrimaryButton
              size={isLg ? 64 : 52}
              type="ghost"
              text={buttonText}
              language={language}
              onClick={() => navigate('/signup')}
            />
          </div>
        </div>
      </div>

    </section>
  );
}


export default SummarySection;
